import {
  cashAlarm,
  checkAlarm,
  varianceAlarm,
  type EngineRow,
  type Model,
  type WorkbookSettings,
} from './engine';
import type { Insight } from './workbookHooks';

const fmtMoney = (cents: number) =>
  (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
const fmtPct = (v: number) => `${(v * 100).toFixed(1)}%`;
const monthLabel = (m: string) =>
  new Date(m).toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' });

/**
 * Rule-based flags computed in the browser. These are always on; the AI
 * pass layers judgement on top of the same facts (see `buildSnapshot`).
 */
export function localInsights(
  model: Model,
  rows: EngineRow[],
  visibleMonths: string[],
  settings: WorkbookSettings,
): Insight[] {
  const out: Insight[] = [];
  const idx = visibleMonths.map((m) => model.months.indexOf(m)).filter((i) => i >= 0);

  // Cash floor
  const lowMonths: string[] = [];
  let negMonth: string | null = null;
  let minCash = Infinity;
  let minMonth = '';
  for (const i of idx) {
    const v = model.endingCash.plan[i];
    if (!model.monthIsClosed[i]) {
      if (v < minCash) {
        minCash = v;
        minMonth = model.months[i];
      }
      const a = cashAlarm(v, settings);
      if (a === 'bad' && !negMonth) negMonth = model.months[i];
      if (a !== 'none') lowMonths.push(model.months[i]);
    }
  }
  if (negMonth) {
    out.push({
      severity: 'critical',
      title: `Projected bank balance goes negative in ${monthLabel(negMonth)}`,
      detail: `Lowest point is ${fmtMoney(minCash)} in ${monthLabel(minMonth)}. The plan as entered cannot be funded from operating cash.`,
      suggestion:
        'Pull forward distributor collections, delay the largest capital items, or add a financing line (owner contribution / line of credit) in the Financing section.',
      row_key: 'calc:ending-cash',
      month: negMonth,
    });
  } else if (lowMonths.length > 0) {
    out.push({
      severity: 'warning',
      title: `Cash dips below the ${fmtMoney(settings.min_cash_cents)} floor in ${lowMonths.length} month${lowMonths.length === 1 ? '' : 's'}`,
      detail: `Lowest projected balance is ${fmtMoney(minCash)} in ${monthLabel(minMonth)} (${lowMonths.map(monthLabel).join(', ')}).`,
      suggestion:
        'Review timing of inventory purchases and payroll against expected receipts for those months.',
      row_key: 'calc:ending-cash',
      month: lowMonths[0],
    });
  } else if (Number.isFinite(minCash)) {
    out.push({
      severity: 'info',
      title: `Cash stays above the floor — lowest ${fmtMoney(minCash)} in ${monthLabel(minMonth)}`,
      detail: 'Every open month in the visible range ends above the minimum cash setting.',
      row_key: 'calc:ending-cash',
      month: minMonth,
    });
  }

  // Gross margin
  const gm = model.ratios.find((r) => r.key === 'ratio:gross-margin');
  if (gm) {
    const below = idx.filter((i) => {
      const v = model.monthIsClosed[i] ? gm.series.actual[i] : gm.series.plan[i];
      return v !== null && v !== 0 && v < settings.gross_margin_floor_pct;
    });
    if (below.length > 0) {
      out.push({
        severity: 'warning',
        title: `Gross margin below ${fmtPct(settings.gross_margin_floor_pct)} in ${below.length} month${below.length === 1 ? '' : 's'}`,
        detail: below
          .slice(0, 6)
          .map((i) => {
            const v = model.monthIsClosed[i] ? gm.series.actual[i] : gm.series.plan[i];
            return `${monthLabel(model.months[i])}: ${fmtPct(v ?? 0)}`;
          })
          .join(' · '),
        suggestion:
          'Check the unit COGS on the Sales Drivers tab against recent landed costs, and whether too many units are priced at the big-distributor tier.',
        row_key: 'ratio:gross-margin',
        month: model.months[below[0]],
      });
    }
  }

  // Variances on closed months — biggest unfavourable first.
  const variances: Array<{
    row: EngineRow;
    month: string;
    plan: number;
    actual: number;
    diff: number;
  }> = [];
  for (const row of rows) {
    if (row.kind !== 'account' && row.kind !== 'total' && row.kind !== 'calc') continue;
    if (row.valueType !== 'currency' || row.favorable === 'none') continue;
    for (const i of idx) {
      if (!model.monthIsClosed[i]) continue;
      const plan = row.series.plan[i];
      const actual = row.series.actual[i];
      if (actual === null) continue;
      const a = varianceAlarm(row, plan, actual, settings);
      if (a === 'bad' || a === 'warn') {
        variances.push({ row, month: model.months[i], plan, actual, diff: actual - plan });
      }
    }
  }
  variances.sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));
  for (const v of variances.slice(0, 5)) {
    const unfavorable = v.row.favorable === 'higher' ? v.diff < 0 : v.diff > 0;
    out.push({
      severity: unfavorable ? 'warning' : 'opportunity',
      title: `${v.row.label}: ${monthLabel(v.month)} actual ${fmtMoney(v.actual)} vs plan ${fmtMoney(v.plan)}`,
      detail: `${unfavorable ? 'Unfavourable' : 'Favourable'} by ${fmtMoney(Math.abs(v.diff))}${
        v.plan ? ` (${fmtPct(Math.abs(v.diff) / Math.abs(v.plan))})` : ''
      }.`,
      suggestion: unfavorable
        ? 'If this is a timing difference, leave the plan; if it is structural, use “Fill right” on the actual to reset the remaining months.'
        : 'Consider raising the plan for the remaining months if this is repeatable.',
      row_key: v.row.key,
      month: v.month,
    });
  }

  // Checks
  for (const c of model.checks) {
    const failing = idx.filter((i) => checkAlarm(c.diffs[i], c.tolerance) === 'bad');
    if (failing.length > 0) {
      out.push({
        severity: 'critical',
        title: `Integrity check failing: ${c.label}`,
        detail: `${c.description} Off in ${failing.map((i) => monthLabel(model.months[i])).join(', ')}.`,
        row_key: c.key,
        month: model.months[failing[0]],
      });
    }
  }

  if (model.hiddenWithActivity > 0) {
    out.push({
      severity: 'info',
      title: `${model.hiddenWithActivity} hidden account${model.hiddenWithActivity === 1 ? ' has' : 's have'} activity`,
      detail:
        'Hidden accounts still roll into totals, so totals may not add up to what is visible on screen.',
      suggestion: 'Use “Hidden” in the sheet toolbar to show them again.',
    });
  }

  for (const w of model.sales.mixWarnings) {
    out.push({ severity: 'warning', title: 'Sales model mix does not total 100%', detail: w });
  }

  // Sales plan gaps: active customers with zero units in every open month.
  const openIdx = idx.filter((i) => !model.monthIsClosed[i]);
  if (openIdx.length > 0) {
    const silent = rows.filter(
      (r) => r.kind === 'customer' && openIdx.every((i) => !(r.series.plan[i] > 0)),
    );
    if (silent.length > 0 && silent.length <= 6) {
      out.push({
        severity: 'info',
        title: `No units planned for ${silent.map((r) => r.label).join(', ')}`,
        detail: 'These customers are active but have no projected units in the visible months.',
        suggestion: 'Enter an estimate or switch the customer off on the Sales Drivers tab.',
        row_key: silent[0].key,
      });
    }
  }

  // Revenue concentration (top customer share of planned units).
  const custRows = rows.filter((r) => r.kind === 'customer');
  const totalUnits = openIdx.reduce((s, i) => s + model.sales.totalUnits.plan[i], 0);
  if (totalUnits > 0 && custRows.length > 1) {
    const top = custRows
      .map((r) => ({ r, u: openIdx.reduce((s, i) => s + r.series.plan[i], 0) }))
      .sort((a, b) => b.u - a.u)[0];
    const share = top.u / totalUnits;
    if (share >= 0.5) {
      out.push({
        severity: 'warning',
        title: `${top.r.label} is ${fmtPct(share)} of planned units`,
        detail: 'A single customer slipping a month would move most of the revenue line.',
        suggestion:
          'Model a downside scenario (clone this scenario and cut that customer by 30–50%).',
        row_key: top.r.key,
      });
    }
  }

  return out;
}

/**
 * Compact, model-agnostic snapshot for the AI edge function. Deliberately
 * small: visible months only, key statement lines in whole dollars, and
 * the rule-based flags so the model can reason past them.
 */
export function buildSnapshot(
  model: Model,
  rows: EngineRow[],
  visibleMonths: string[],
  settings: WorkbookSettings,
  scenarioName: string,
) {
  const idx = visibleMonths.map((m) => model.months.indexOf(m)).filter((i) => i >= 0);
  const dollars = (c: number | null) => (c === null ? null : Math.round(c / 100));
  const series = (
    s: { plan: number[]; actual: (number | null)[] },
    scale: 'money' | 'raw' = 'money',
  ) => ({
    plan: idx.map((i) => (scale === 'money' ? dollars(s.plan[i]) : round2(s.plan[i]))),
    actual: idx.map((i) => (scale === 'money' ? dollars(s.actual[i]) : round2(s.actual[i]))),
  });
  const keyRows = rows.filter(
    (r) =>
      (r.kind === 'total' && r.depth === 0) ||
      r.kind === 'calc' ||
      r.kind === 'ratio' ||
      (r.kind === 'account' && !r.isEmpty && r.depth <= 2),
  );
  return {
    scenario: scenarioName,
    generated_at: new Date().toISOString(),
    months: visibleMonths,
    closed: idx.map((i) => model.monthIsClosed[i]),
    settings: {
      min_cash: dollars(settings.min_cash_cents),
      variance_pct_threshold: settings.variance_pct_threshold,
      gross_margin_floor_pct: settings.gross_margin_floor_pct,
    },
    statements: {
      revenue: series(model.revenue),
      gross_profit: series(model.grossProfit),
      operating_income: series(model.operatingIncome),
      net_income: series(model.netIncome),
      cash_flow: series(model.cashFlow),
      ending_cash: series(model.endingCash),
      ratios: model.ratios.map((r) => ({ key: r.key, label: r.label, ...series(r.series, 'raw') })),
    },
    lines: keyRows.slice(0, 120).map((r) => ({
      key: r.key,
      label: r.label,
      group: r.group,
      kind: r.kind,
      value_type: r.valueType,
      ...(r.valueType === 'currency' ? series(r.series) : series(r.series, 'raw')),
      totals: {
        plan: r.valueType === 'currency' ? dollars(r.totals.plan) : round2(r.totals.plan),
        actual: r.valueType === 'currency' ? dollars(r.totals.actual) : round2(r.totals.actual),
        forecast:
          r.valueType === 'currency' ? dollars(r.totals.forecast) : round2(r.totals.forecast),
        variance:
          r.valueType === 'currency' ? dollars(r.totals.variance) : round2(r.totals.variance),
      },
    })),
    sales: {
      total_units: series(model.sales.totalUnits, 'raw'),
      customers: rows
        .filter((r) => r.kind === 'customer')
        .map((r) => ({
          key: r.key,
          name: r.label,
          tier: r.hint,
          units: idx.map((i) => r.series.plan[i]),
        })),
      products: model.sales.byProduct.map((b) => ({
        product_id: b.productId,
        units: idx.map((i) => b.units[i]),
        revenue: idx.map((i) => dollars(b.revenueCents[i])),
        cogs: idx.map((i) => dollars(b.cogsCents[i])),
      })),
      mix_warnings: model.sales.mixWarnings,
    },
    checks: model.checks.map((c) => ({
      key: c.key,
      label: c.label,
      failing_months: idx
        .filter((i) => checkAlarm(c.diffs[i], c.tolerance) === 'bad')
        .map((i) => model.months[i]),
    })),
    flags: localInsights(model, rows, visibleMonths, settings),
  };
}

function round2(v: number | null): number | null {
  if (v === null) return null;
  return Math.round(v * 100) / 100;
}
